import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import type { Message } from 'shared';
import { useMessages } from '@/hooks/useMessages';

/** RFC3339 → European Portuguese date/time string. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EstadoBadge({ estado }: { estado: Message['estado'] }) {
  return estado === 'Ativo' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-available-soft text-available">
      Ativo
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      Inativo
    </span>
  );
}

export default function NotificationsPage() {
  const { data, isLoading, isError } = useMessages({ activeOnly: true, currentOnly: true });

  // Go marshals empty slices as JSON null — normalise to an array.
  const messages = useMemo(() => data?.messages ?? [], [data]);

  return (
    <>
      <Helmet>
        <title>Notificações | AICD</title>
      </Helmet>
      <div className="space-y-6">
        <header className="space-y-3">
          <Link
            to="/profile"
            aria-label="Voltar ao perfil"
            className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <BackGlyph />
            Voltar
          </Link>
          <div>
            <h1 className="text-2xl font-black text-foreground">Notificações</h1>
          </div>
        </header>

        <section aria-labelledby="notificacoes-heading">
          <h2 id="notificacoes-heading" className="sr-only">
            Lista de notificações
          </h2>

          {isLoading && <NotificationsSkeleton />}

          {isError && !isLoading && (
            <div
              role="alert"
              className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
            >
              Não foi possível carregar as notificações. Tente novamente mais tarde.
            </div>
          )}

          {!isLoading && !isError && messages.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border-strong bg-card p-6 text-center text-sm text-muted-foreground">
              Ainda não tem notificações.
            </div>
          )}

          {!isLoading && !isError && messages.length > 0 && (
            <ul className="space-y-3">
              {messages.map((message) => (
                <li
                  key={message.id}
                  className="rounded-2xl border border-border bg-card p-4 shadow-sm space-y-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-foreground">{message.descricao}</p>
                    <EstadoBadge estado={message.estado} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(message.dataInicio)} – {formatDate(message.dataFim)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

function BackGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5}>
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NotificationsSkeleton() {
  return (
    <ul className="space-y-3" role="status" aria-live="polite" aria-label="A carregar notificações">
      {[0, 1, 2].map((i) => (
        <li key={i} className="rounded-2xl border border-border bg-card p-4 space-y-2">
          <div className="h-3 w-3/4 animate-pulse rounded bg-input-soft" />
          <div className="h-2 w-1/2 animate-pulse rounded bg-input-soft" />
        </li>
      ))}
    </ul>
  );
}
